import { Engine, type RuleProperties } from "json-rules-engine";

import characterInBrandOperator from "@rules/operators/characterInBrand";
import isArrayOfMinLengthOperator from "@rules/operators/isArrayOfMinLength";
import regexMatchesOperator from "@rules/operators/regexMatches";
import type { Rule, RuleEvaluationResult, RuleFacts } from "@rules/types";

/**
 * Build a fresh json-rules-engine `Engine` pre-loaded with our custom operators.
 *
 * Why a factory: `Engine` is stateful — facts and operators register onto the
 * instance — so each evaluation call gets its own engine to avoid cross-talk.
 */
const createEngine = (): Engine => {
  const engine = new Engine([], { allowUndefinedFacts: true });
  engine.addOperator(...regexMatchesOperator);
  engine.addOperator(...isArrayOfMinLengthOperator);
  engine.addOperator(...characterInBrandOperator);
  return engine;
};

const ensureRuleHasName = (rule: Rule): Rule => ({ ...rule, name: rule.name || rule.id });

/**
 * Evaluate a set of rules against a fact bag. Returns the events of rules
 * whose conditions matched, sorted by descending priority (json-rules-engine default).
 *
 * Public entry point for both runtime branching and the ad-review lint.
 */
export const evaluateRules = async (
  rules: ReadonlyArray<Rule>,
  facts: RuleFacts,
): Promise<RuleEvaluationResult> => {
  const engine = createEngine();

  for (const rule of rules) {
    engine.addRule(ensureRuleHasName(rule) as unknown as RuleProperties);
  }

  const result = await engine.run(facts);

  const events = result.events.map((event) => ({
    type: event.type,
    params: event.params,
  }));

  const firedRuleIds = result.results
    .filter((ruleResult) => ruleResult.result)
    .map((ruleResult) => ruleResult.name)
    .filter((name): name is string => typeof name === "string");

  return { events, firedRuleIds };
};

export type {
  Rule,
  RuleCondition,
  RuleConditionGroup,
  RuleEvaluationResult,
  RuleFacts,
  RuleOperator,
} from "@rules/types";
export { default as regexMatchesOperator } from "@rules/operators/regexMatches";
export { default as isArrayOfMinLengthOperator } from "@rules/operators/isArrayOfMinLength";
export { default as characterInBrandOperator } from "@rules/operators/characterInBrand";
export { AD_REVIEW_LINT_RULES, evaluateAdReviewLint } from "@rules/adReviewLint";
export type {
  AdReviewLintInput,
  LintReport,
  LintSeverity,
  LintViolation,
} from "@rules/adReviewLint";
